import { VISITED_STATUSES } from '../types';
import type { Id } from '../types';
import { groupChecks, learnSequence, type RouteCheck } from './learn';
import { ALGO_VERSION } from './model';
import type { FoldStoreOptions, OrderEvent, OrderEventOf, ProductPos, StoreModel } from './model';
import { collides, compareIds, keepSeparated, medianGapOf, renumber, withoutGuessed, type Positions } from './positions';

type CheckEvent = OrderEventOf<'item_checked'>;

const VISITED = new Set<string>(VISITED_STATUSES);

function compareEvents(a: OrderEvent, b: OrderEvent): number {
  return compareIds(a.hlc, b.hlc) || compareIds(a.id, b.id);
}

function learnVisit(byProduct: Positions, checks: readonly CheckEvent[], resolve: (id: Id) => Id): void {
  const perDevice = new Map<Id, CheckEvent[]>();
  for (const c of checks) {
    const list = perDevice.get(c.deviceId);
    if (list) list.push(c);
    else perDevice.set(c.deviceId, [c]);
  }
  for (const device of [...perDevice.keys()].sort(compareIds)) {
    // Sortowanie stabilne: przy równym seq zostaje kolejność (hlc, id).
    const ordered = perDevice.get(device)!.sort((a, b) => a.seq - b.seq);
    const seen = new Set<Id>();
    const route: RouteCheck[] = [];
    for (const c of ordered) {
      const productId = resolve(c.productId);
      if (seen.has(productId)) continue;
      seen.add(productId);
      route.push({ productId, wallMs: c.wallMs });
    }
    const groups = groupChecks(route);
    if (groups) learnSequence(byProduct, groups);
  }
}

function moveManually(byProduct: Positions, productId: Id, afterId: Id | null, beforeId: Id | null): void {
  const prev = byProduct.get(productId);
  byProduct.delete(productId);
  const posOf = (id: Id | null) => (id === null ? undefined : byProduct.get(id)?.pos);
  const place = (): number => {
    const after = posOf(afterId);
    const before = posOf(beforeId);
    if (after !== undefined && before !== undefined) return (after + before) / 2;
    if (before !== undefined) return before - medianGapOf(withoutGuessed(byProduct.values()));
    if (after !== undefined) return after + medianGapOf(withoutGuessed(byProduct.values()));
    return 0;
  };
  let pos = place();
  // Sąsiedzi z równą pozycją (np. z serii) — najpierw ich rozsuwamy, inaczej środek zlałby się z nimi.
  if (collides(byProduct, pos)) {
    renumber(byProduct);
    pos = place();
  }
  byProduct.set(productId, {
    pos,
    n: Math.max(prev?.n ?? 0, 1),
    manual: true,
    contra: 0,
    // Ręczna poprawka to potwierdzenie — pozycja nie jest już tylko kopią z innego sklepu.
    seeded: false,
  });
}

/**
 * Zgadnięte miejsce nowego produktu: tuż za afterId (null = przed pierwszym). Działa tylko dla produktu,
 * którego model nie zna; nieznany afterId pomija wpis. Pozycja nie jest wizytą — n = 0, nauka jej nie ufa.
 */
function placeGuessed(byProduct: Positions, productId: Id, afterId: Id | null): void {
  if (byProduct.has(productId) || (afterId !== null && !byProduct.has(afterId))) return;
  const place = (): number => {
    if (afterId === null) {
      let first = Infinity;
      for (const p of byProduct.values()) if (p.pos < first) first = p.pos;
      return first === Infinity ? 0 : first - medianGapOf(withoutGuessed(byProduct.values()));
    }
    const after = byProduct.get(afterId)!.pos;
    // Następnik to najbliższa dalsza pozycja: remisy z afterId (seria) to ta sama półka, produkt staje za nimi.
    let next = Infinity;
    for (const p of byProduct.values()) if (p.pos > after && p.pos < next) next = p.pos;
    return next === Infinity ? after + medianGapOf(withoutGuessed(byProduct.values())) : (after + next) / 2;
  };
  let pos = place();
  if (collides(byProduct, pos)) {
    renumber(byProduct);
    pos = place();
  }
  byProduct.set(productId, { pos, n: 0, manual: false, contra: 0, seeded: false, guessed: true });
}

function isPlacement(p: unknown): p is { productId: Id; afterId: Id | null } {
  if (typeof p !== 'object' || p === null) return false;
  const { productId, afterId } = p as Record<string, unknown>;
  return typeof productId === 'string' && (afterId === null || typeof afterId === 'string');
}

function isSnapshotEntry(entry: unknown): entry is [Id, number] {
  return Array.isArray(entry) && typeof entry[0] === 'string' && Number.isFinite(entry[1]);
}

function toRecord(byProduct: Positions): Record<Id, ProductPos> {
  const out: Record<Id, ProductPos> = {};
  for (const id of [...byProduct.keys()].sort(compareIds)) out[id] = { ...byProduct.get(id)! };
  return out;
}

/**
 * Odtwarza model kolejności sklepu z dziennika zdarzeń. Wynik nie zależy od kolejności,
 * w jakiej zdarzenia dotarły — liczy się tylko porządek (hlc, id).
 */
export function foldStore(storeId: Id, events: readonly OrderEvent[], opts: FoldStoreOptions = {}): StoreModel {
  const resolve = opts.resolveProduct ?? ((id: Id) => id);
  const resolveStore = opts.resolveStore ?? ((id: Id) => id);
  const log = events.filter((e) => resolveStore(e.storeId) === storeId).sort(compareEvents);
  const byProduct: Positions = new Map();
  const open = new Map<Id, CheckEvent[]>();
  const finished = new Set<Id>();
  const undone = new Set<Id>();
  let seededFrom: Id | undefined;
  let seededAuto = false;

  for (const e of log) {
    switch (e.kind) {
      case 'item_checked': {
        // Odhaczenia po zakończeniu wizyty już niczego nie uczą.
        if (finished.has(e.visitId) || !VISITED.has(e.status)) break;
        const list = open.get(e.visitId);
        if (list) list.push(e);
        else open.set(e.visitId, [e]);
        break;
      }
      case 'undo':
        undone.add(e.targetId);
        break;
      case 'visit_finished': {
        if (finished.has(e.visitId)) break;
        finished.add(e.visitId);
        const checks = open.get(e.visitId);
        open.delete(e.visitId);
        if (!checks) break;
        learnVisit(
          byProduct,
          checks.filter((c) => !undone.has(c.id)),
          resolve,
        );
        keepSeparated(byProduct);
        break;
      }
      case 'manual_move':
        moveManually(
          byProduct,
          resolve(e.productId),
          e.afterId === null ? null : resolve(e.afterId),
          e.beforeId === null ? null : resolve(e.beforeId),
        );
        keepSeparated(byProduct);
        break;
      case 'seeded': {
        if (typeof e.fromStoreId !== 'string' || !Array.isArray(e.snapshot)) break;
        const from = resolveStore(e.fromStoreId);
        // Seed ze sklepu scalonego z tym — jego prawdziwa historia już jest w dzienniku.
        if (from === storeId) break;
        // Kopia automatyczna przegrywa z wcześniejszą wizytą albo kopią (np. z drugiego telefonu).
        if (e.ifEmpty === true && withoutGuessed(byProduct.values()).length > 0) break;
        for (const entry of e.snapshot as unknown[]) {
          if (!isSnapshotEntry(entry)) continue;
          const id = resolve(entry[0]);
          const current = byProduct.get(id);
          // Znanych pozycji seed nie rusza; zgadnięte ustępują kopii.
          if (current && !current.guessed) continue;
          byProduct.set(id, { pos: entry[1], n: 1, manual: false, contra: 0, seeded: true });
        }
        seededFrom = from;
        seededAuto = e.ifEmpty === true;
        keepSeparated(byProduct);
        break;
      }
      case 'placed':
        if (!Array.isArray(e.placements)) break;
        // Wpisy po kolei — następny może stać za poprzednim.
        for (const p of e.placements as unknown[]) {
          if (!isPlacement(p)) continue;
          placeGuessed(byProduct, resolve(p.productId), p.afterId === null ? null : resolve(p.afterId));
        }
        keepSeparated(byProduct);
        break;
      case 'reset':
        byProduct.clear();
        seededFrom = undefined;
        seededAuto = false;
        break;
    }
  }

  return {
    algoVersion: ALGO_VERSION,
    storeId,
    eventCount: log.length,
    lastHlc: log.at(-1)?.hlc ?? null,
    byProduct: toRecord(byProduct),
    ...(seededFrom === undefined ? {} : { seededFrom }),
    ...(seededAuto ? { seededAuto: true as const } : {}),
  };
}
